import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogPortal,
} from "@/components/ui/dialog";
import { SectionConfigWithMetadata, ColumnOption } from "@/types";
import { DataGraphResponse } from "@/screens/creatives/types";
import Metrics from "@/components/common/metrics";
import { RiArrowDownSLine } from "@remixicon/react";
import { formatDateRange } from "@/utils/dateUtils";
import { useGlobalStore } from "@/GlobalStore";
import { useParams } from "react-router-dom";
import axios from "axios";
import { getDataGraph } from "@/screens/creatives/apiService";
import { getOptimumInterval, formatGraphDateLabel } from "@/utils/dateUtils";
import BreakdownBy from "../../configs/BreakdownBy";
import {
  SqSelect,
  SqSelectContent,
  SqSelectItem,
  SqSelectTrigger,
} from "@/components/squircle-ui/select";
import { GraphIntervals } from "@/constants";
import { LineChart } from "@/components/ui/tremor-charts/LineChart";
import { AreaChart } from "@/components/ui/tremor-charts/AreaChart";
import { BarChart } from "@/components/ui/tremor-charts/BarChart";
import WaveLoader from "@/components/common/loaders/WaveLoader";
import ErrorState from "@/components/common/errorStates/ErrorState";
import { differenceInDays } from "date-fns";
import { IconDatabase } from "@tabler/icons-react";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";

interface GraphDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  metrics: ColumnOption[];
  configWithReportMetadata: SectionConfigWithMetadata;
  defaultSelectedMetric: string | null;
  sectionId: string;
  initialGraphData: DataGraphResponse;
  currentPeriodColor?: "red" | "emerald";
  previousPeriodColor?: "blue" | "red" | "emerald";
}

export const GraphDialog: React.FC<GraphDialogProps> = ({
  open,
  onOpenChange,
  metrics,
  configWithReportMetadata,
  defaultSelectedMetric,
  sectionId,
  initialGraphData,
  currentPeriodColor = "emerald",
  previousPeriodColor = "blue",
}) => {
  const { selectedAccount } = useGlobalStore();
  const [selectedMetric, setSelectedMetric] = useState<string>(
    defaultSelectedMetric ?? metrics?.[0]?.key ?? undefined,
  );

  const [selectedBreakdown, setSelectedBreakdown] = useState<string | null>(null);
  const [interval, setInterval] = useState<string>(
    getOptimumInterval({
      start_date: configWithReportMetadata?.date?.start_date ?? "",
      end_date: configWithReportMetadata?.date?.end_date ?? "",
    }),
  );
  const [graphType, setGraphType] = useState<"line" | "bar">("line");

  // graph data
  const { accountId } = useParams();
  const [loadingGraph, setLoadingGraph] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [graphData, setGraphData] = useState<DataGraphResponse>(initialGraphData);

  useEffect(() => {
    // on open reset things
    if (open) {
      if (defaultSelectedMetric) setSelectedMetric(defaultSelectedMetric);
      setSelectedBreakdown(null);
      setInterval(
        getOptimumInterval({
          start_date: configWithReportMetadata?.date?.start_date ?? "",
          end_date: configWithReportMetadata?.date?.end_date ?? "",
        }),
      );
      setGraphType("line");
      setGraphData(initialGraphData);
      setLoadingGraph(false);
      setHasError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isMounted = useRef(true);

  // Single timeout reference
  const [pollingTimeout, setPollingTimeout] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  // Cleanup effect
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
    };
  }, [pollingTimeout]);

  // Clear polling timeout helper
  const clearPollingTimeout = useCallback(() => {
    if (pollingTimeout) {
      clearTimeout(pollingTimeout);
      setPollingTimeout(null);
    }
  }, [pollingTimeout]);

  const fetchGraphData = async (
    breakdownValue: string | null,
    intervalValue: string,
    isPolling?: boolean,
  ) => {
    const cancelToken = axios.CancelToken.source();
    if (!accountId) return;
    if (!isPolling) {
      setLoadingGraph(true);
    }

    try {
      const response = await getDataGraph(
        accountId,
        sectionId,
        intervalValue,
        {
          ...(configWithReportMetadata ?? {}),
          dropdowns: {
            ...configWithReportMetadata?.dropdowns,
            group_by: "aggregated",
            breakdown_by: breakdownValue,
          },
        },
        cancelToken.token,
      );

      // If component unmounted during request, don't proceed
      if (!isMounted.current) return;

      // Check response status and set up polling if needed
      if (response?.status === "pending" || response?.status === "partial") {
        setGraphData(response);
        setHasError(false);
        // Set up next polling iteration
        const timeoutId = setTimeout(() => {
          void fetchGraphData(breakdownValue, intervalValue, true);
        }, 5000);

        setPollingTimeout(timeoutId);
      } else if (response?.status === "failed") {
        setGraphData(response);
        setLoadingGraph(false);
        setHasError(true);
        clearPollingTimeout();
      } else {
        setGraphData(response);
        setLoadingGraph(false);
        setHasError(false);
        clearPollingTimeout();
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Error fetching graph data:", error);
        setLoadingGraph(false);
        setHasError(true);
        clearPollingTimeout();
      }
    }
  };

  const handleBreakdownChange = (value: string | null) => {
    setSelectedBreakdown(value);
    void fetchGraphData(value, interval, false);
  };

  const handleIntervalChange = (value: string) => {
    setInterval(value);
    void fetchGraphData(selectedBreakdown, value, false);
  };

  const handleMetricChange = (value: string) => setSelectedMetric(value);

  // Handler for Metrics component - convert ColumnOption[] to string
  const handleMetricsChange = useCallback((selectedMetrics: ColumnOption[]) => {
    if (selectedMetrics.length > 0) {
      handleMetricChange(selectedMetrics[0].key);
    }
  }, []);

  // Handler for selected metrics - convert string to ColumnOption[]
  const getSelectedMetrics = useMemo(() => {
    const selectedMetricObj = metrics.find((m) => m.key === selectedMetric);
    if (!selectedMetricObj) return [];

    // Convert Column to ColumnOption format
    return [selectedMetricObj];
  }, [metrics, selectedMetric]);

  const dataConfigurations = useGlobalStore((s) => s.dataConfigurations);
  const breakdownByOptions =
    dataConfigurations?.dropdowns
      ?.find((d) => d.key === "breakdown_by")
      ?.options.map((o) => ({
        key: o.key,
        label: o.label,
        show_platforms: !!o?.show_platforms,
        platforms: o?.platforms ?? [],
      })) ?? [];

  const selectedDateDays = useMemo(() => {
    if (
      !configWithReportMetadata?.date?.start_date ||
      !configWithReportMetadata?.date?.end_date
    ) {
      return 0;
    }
    return differenceInDays(
      new Date(configWithReportMetadata.date.end_date),
      new Date(configWithReportMetadata.date.start_date),
    );
  }, [
    configWithReportMetadata?.date?.start_date,
    configWithReportMetadata?.date?.end_date,
  ]);
  const compareDateDays = useMemo(() => {
    if (
      !configWithReportMetadata?.compare_date?.start_date ||
      !configWithReportMetadata?.compare_date?.end_date
    ) {
      return 0;
    }
    return differenceInDays(
      new Date(configWithReportMetadata.compare_date.end_date),
      new Date(configWithReportMetadata.compare_date.start_date),
    );
  }, [
    configWithReportMetadata?.compare_date?.start_date,
    configWithReportMetadata?.compare_date?.end_date,
  ]);

  const isSamePeriod = compareDateDays === selectedDateDays;

  // Helper function to format date labels
  const formatDataPointLabel = useCallback(
    (
      dp: { x: string },
      intervalRangesMap: Map<string, { start_date: string; end_date: string }>,
    ) => {
      const intervalRange = intervalRangesMap.get(dp.x);
      return intervalRange
        ? formatGraphDateLabel(
            intervalRange.start_date,
            intervalRange.end_date,
            interval,
            selectedAccount?.time_zone,
          )
        : dp.x;
    },
    [interval, selectedAccount?.time_zone],
  );

  // Helper function to process data entries and extract metric values
  const processDataEntries = (
    entries: [string, unknown][],
    suffix: string,
    dataPoint: { [key: string]: number | string },
    selectedMetric: string,
  ) => {
    entries.forEach(([key, value]) => {
      if (
        key !== "x" &&
        typeof value === "object" &&
        value !== null &&
        selectedMetric in value
      ) {
        const breakdown = key.split("[@breakdown@]").at(-1);
        const label =
          breakdown !== "aggregated" ? `${breakdown} — ${suffix}` : `${suffix} Period`;
        dataPoint[label] = (value as Record<string, unknown>)[selectedMetric] as number;
      }
    });
  };

  // Memoize the maps for better performance
  const intervalRangesMap = useMemo(
    () =>
      new Map(
        graphData?.data?.interval_ranges?.map((range) => [range.label, range]) || [],
      ),
    [graphData?.data?.interval_ranges],
  );

  const previousDataMap = useMemo(
    () => new Map(graphData?.data?.data_points_previous?.map((dp) => [dp.x, dp]) || []),
    [graphData?.data?.data_points_previous],
  );

  const formattedGraphData = useMemo(() => {
    if (!graphData?.data?.data_points || !selectedMetric) return [];
    return graphData.data.data_points.map((dp) => {
      const customLabel = formatDataPointLabel(dp, intervalRangesMap);
      const dataPoint: { [key: string]: number | string } = { x: customLabel };
      // Process current period data
      processDataEntries(Object.entries(dp), "Current", dataPoint, selectedMetric);
      // Process previous period data if available and periods match
      const matchingPrevious = previousDataMap.get(dp.x);
      if (isSamePeriod && matchingPrevious) {
        processDataEntries(
          Object.entries(matchingPrevious),
          "Previous",
          dataPoint,
          selectedMetric,
        );
      }
      return dataPoint;
    });
  }, [
    graphData?.data?.data_points,
    selectedMetric,
    isSamePeriod,
    intervalRangesMap,
    previousDataMap,
    formatDataPointLabel,
  ]);

  // Extract all keys other than 'x' for categories
  const allKeys = useMemo(() => {
    if (!formattedGraphData?.length) return [];
    // Collect all unique keys from all data points, excluding 'x'
    const keySet = new Set<string>();
    formattedGraphData.forEach((dataPoint) => {
      Object.keys(dataPoint).forEach((key) => {
        if (key !== "x") keySet.add(key);
      });
    });
    return Array.from(keySet);
  }, [formattedGraphData]);

  const selectedMetricObj = metrics.find((m) => m.key === selectedMetric);
  const valuePrefix = selectedMetricObj?.prefix;
  const valueSuffix = selectedMetricObj?.suffix;

  const valueFormatter = useCallback(
    (value: number) => {
      // Use built-in Intl.NumberFormat for compact notation
      const formatter = new Intl.NumberFormat("en-US", {
        notation: "compact",
        compactDisplay: "short",
      });

      const formattedValue = formatter.format(value);
      // No space between prefix and value to prevent Y-axis label line breaking (e.g. "$" on separate line)
      return valuePrefix && valueSuffix
        ? `${valuePrefix}${formattedValue} ${valueSuffix}`
        : valuePrefix
          ? `${valuePrefix}${formattedValue}`
          : valueSuffix
            ? `${formattedValue} ${valueSuffix}`
            : formattedValue;
    },
    [valuePrefix, valueSuffix],
  );

  // Determine colors for Area Chart when there's no breakdown
  const chartColors = useMemo(() => {
    // Only apply custom colors when there's no breakdown
    if (selectedBreakdown) {
      return undefined; // Let chart use default colors
    }

    // Only apply custom colors if we have the expected "Current" period category
    const hasCurrentPeriod = allKeys.some((key) => key.includes("Current"));

    if (!hasCurrentPeriod) {
      return undefined; // Fallback to default colors if unexpected format
    }

    // Map colors based on category names
    const colors = allKeys.map((key) => {
      if (key.includes("Current")) {
        return currentPeriodColor;
      } else if (key.includes("Previous")) {
        return previousPeriodColor;
      }
      // This shouldn't happen with expected data format, return undefined for safety
      return undefined;
    });

    // Filter out undefined values - if any exist, return undefined to use defaults
    return colors.some((c) => c === undefined)
      ? undefined
      : (colors as ("red" | "blue" | "emerald")[]);
  }, [allKeys, selectedBreakdown, currentPeriodColor, previousPeriodColor]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
        <DialogPortal>
          {/* Custom overlay (default overlay was blocking cursor event on dropdowns) */}
          {open && (
            <div
              className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
              onClick={() => onOpenChange(false)}
            />
          )}

          <DialogContent
            hideCloseButton
            hideOverlay
            className="max-w-[900px] overflow-visible pb-2 px-2"
          >
            <DialogHeader aria-hidden className="hidden">
              <DialogTitle aria-hidden />
            </DialogHeader>
            <div className="flex justify-between overflow-visible pl-4 pr-4">
              <div className="flex flex-col gap-0">
                <div className="w-fit cursor-pointer">
                  <Metrics
                    renderingStyle="single_select"
                    selectedMetrics={getSelectedMetrics}
                    onMetricChange={handleMetricsChange}
                    className="text-[12px]"
                    metricsOptions={metrics}
                    trigger={
                      <SqTooltipProvider>
                        <SqTooltip>
                          <SqTooltipTrigger className="cursor-pointer" tabIndex={-1}>
                            <div className="flex items-center gap-0 w-fit cursor-pointer">
                              {getSelectedMetrics?.[0]?.sub_type === "EXTERNAL" && (
                                <IconDatabase
                                  size={16}
                                  className={`text-[#02185999] shrink-0 mr-1`}
                                />
                              )}
                              <span className="font-medium text-[16px] leading-[28px] align-middle text-[#374151] translate-y-[1px] mr-2 truncate max-w-[200px]">
                                {getSelectedMetrics?.[0]?.label ?? "Select metric"}
                              </span>
                              {getSelectedMetrics?.[0]?.show_platforms && (
                                <span className="text-[#FF4500] shrink-0 mr-2">*</span>
                              )}
                              <RiArrowDownSLine
                                size={14}
                                className="shrink-0"
                                color="#9aa3bd"
                              />
                            </div>
                          </SqTooltipTrigger>
                          {getSelectedMetrics?.[0]?.show_platforms &&
                            getSelectedMetrics?.[0]?.platforms &&
                            getSelectedMetrics?.[0]?.platforms.length > 0 && (
                              <SqTooltipContent
                                portal={true}
                                side="bottom"
                                align="start"
                                alignOffset={20}
                              >
                                <p>
                                  {getSelectedMetrics[0]?.label}
                                  <br />
                                  Available for{" "}
                                  {getSelectedMetrics[0]?.platforms
                                    .map(
                                      (platform) =>
                                        platform.charAt(0).toUpperCase() +
                                        platform.slice(1).toLowerCase(),
                                    )
                                    .join(", ")}
                                </p>
                              </SqTooltipContent>
                            )}
                        </SqTooltip>
                      </SqTooltipProvider>
                    }
                  />
                </div>
                <div>
                  {isSamePeriod ? (
                    <>
                      <span className="text-[#021859CC] font-medium text-[11px] leading-[19.16px] tracking-[0.02em] mr-1">
                        {formatDateRange({
                          ...configWithReportMetadata?.date,
                          timezone: selectedAccount?.time_zone,
                        })}
                      </span>
                      <span className="font-normal not-italic text-[11px] leading-[19.16px] tracking-[0.02em] text-[#02185999] mr-1">
                        vs
                      </span>
                      <span className="font-normal not-italic text-[11px] leading-[19.16px] tracking-[0.02em] text-[#02185999] mr-2">
                        {formatDateRange({
                          ...configWithReportMetadata?.compare_date,
                          timezone: selectedAccount?.time_zone,
                        })}
                      </span>
                    </>
                  ) : (
                    <span
                      className="font-normal not-italic text-[12px] leading-[20px] align-middle text-brand-primary-new/80"
                      style={{
                        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                        letterSpacing: "0%",
                      }}
                    >
                      The current and previous periods must be the same length to compare
                      on chart.
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {breakdownByOptions?.length > 0 && (
                  <BreakdownBy
                    value={selectedBreakdown}
                    onChange={handleBreakdownChange}
                    options={breakdownByOptions}
                    triggerClassName="h-7"
                    triggerInnerClassName="!h-5"
                  />
                )}
                <SqSelect value={interval} onValueChange={handleIntervalChange}>
                  <SqSelectTrigger className="h-7 text-[11px] w-[86px] gap-1 text-brand-primary-new/80 px-2 py-1">
                    {GraphIntervals?.find((gI) => gI.value === interval)?.label ?? ""}
                  </SqSelectTrigger>
                  <SqSelectContent radius={12}>
                    {GraphIntervals.map((gI) => (
                      <SqSelectItem
                        value={gI.value}
                        key={gI.value}
                        className="text-[11px] text-brand-primary-new/80"
                      >
                        {gI.label}
                      </SqSelectItem>
                    ))}
                  </SqSelectContent>
                </SqSelect>
                <SqSelect
                  value={graphType}
                  onValueChange={(value: "line" | "bar") => setGraphType(value)}
                >
                  <SqSelectTrigger className="h-7 text-[11px] w-[86px] gap-1 text-brand-primary-new/80 px-2 py-1">
                    {graphType === "line" && "Line Chart"}
                    {graphType === "bar" && "Bar Chart"}
                  </SqSelectTrigger>
                  <SqSelectContent radius={12}>
                    <SqSelectItem
                      value={"line"}
                      key={"line"}
                      className="text-[11px] text-brand-primary-new/80"
                    >
                      Line Chart
                    </SqSelectItem>

                    <SqSelectItem
                      value={"bar"}
                      key={"bar"}
                      className="text-[11px] text-brand-primary-new/80"
                    >
                      Bar Chart
                    </SqSelectItem>
                  </SqSelectContent>
                </SqSelect>
              </div>
            </div>

            {loadingGraph ? (
              <div className="h-[320px] flex items-center justify-center">
                <WaveLoader />
              </div>
            ) : hasError || graphData.status === "failed" ? (
              <div className="h-[320px] flex items-center justify-center">
                <ErrorState
                  onRetry={() => {
                    void fetchGraphData(selectedBreakdown, interval, false);
                  }}
                />
              </div>
            ) : (
              <div className="h-[320px] w-full overflow-visible">
                {graphType === "line" ? (
                  <>
                    {allKeys.length <= 2 ? (
                      <AreaChart
                        className="h-full w-full"
                        data={formattedGraphData}
                        index="x"
                        categories={allKeys}
                        colors={chartColors}
                        valueFormatter={valueFormatter}
                        showXAxis={true}
                        showYAxis={true}
                        showGridLines={true}
                        showTooltip={true}
                        showLegend={allKeys.length <= 4 ? true : false}
                        startEndOnly={false}
                        fill="gradient"
                        yAxisWidth={20}
                      />
                    ) : (
                      <LineChart
                        className="h-full w-full"
                        data={formattedGraphData}
                        index="x"
                        categories={allKeys}
                        valueFormatter={valueFormatter}
                        showXAxis={true}
                        showYAxis={true}
                        showGridLines={true}
                        showTooltip={true}
                        showLegend={allKeys.length <= 4 ? true : false}
                        startEndOnly={false}
                        yAxisWidth={20}
                      />
                    )}
                  </>
                ) : (
                  <BarChart
                    className="h-full w-full"
                    data={formattedGraphData}
                    index="x"
                    categories={allKeys}
                    colors={chartColors}
                    valueFormatter={valueFormatter}
                    showXAxis={true}
                    showYAxis={true}
                    showGridLines={true}
                    showTooltip={true}
                    showLegend={allKeys.length <= 4 ? true : false}
                    startEndOnly={false}
                    layout="horizontal"
                    type="default"
                    yAxisWidth={50}
                  />
                )}
              </div>
            )}
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  );
};
